/**
 * <variant-swatch-availability>
 *
 * Keeps the dependent option(s) valid when the shopper changes a swatch/chip
 * (button-style) option — e.g. Colour — on a product that does NOT use dye lots.
 *
 * The problem it solves:
 *   The theme leaves the other option <select>s on their previous value when the
 *   colour changes, so `colour + staleSize` can resolve to a variant that doesn't
 *   exist. The Add-to-Cart button then reads "Unavailable" — even for a colour that
 *   is perfectly purchasable in other sizes — and it contradicts the colour's
 *   "Currently Out of Stock" label (which is colour-level, see
 *   product-out-of-stock-label.js).
 *
 * What it does:
 *   On a swatch/chip change, if the current full option combination has NO matching
 *   variant, it snaps the *other* option(s) to a real variant for the chosen value —
 *   preferring an in-stock one so the button reads "Add to Cart", and falling back to
 *   any existing variant so it reads "Sold out" (matching the out-of-stock label)
 *   rather than "Unavailable".
 *
 *   It only intervenes when the current combination resolves to nothing, so
 *   full-matrix products still preserve the shopper's other selections (e.g. keep
 *   size "L" when switching colour, showing "Sold out" if that exact combo is out).
 *
 * Scope:
 *   - Reads the authoritative variant list from `[data-variant-json]` (rendered by
 *     smd-product-block-variant-picker.liquid under dynamic product options).
 *   - Drives the hidden option <select>s (the real controls ProductForm reads) and
 *     dispatches `change` so the theme re-resolves the variant, button, price + image.
 *   - Yarn products manage option2 via <dye-lot-picker>, so this module is NOT
 *     rendered for them (see the Liquid gate) to avoid the two fighting over option2.
 *   - No-ops on products with no button-style option (e.g. two plain dropdowns), where
 *     the theme already disables invalid values in the dropdown itself.
 */
(function () {
  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('variant-swatch-availability')) return;

  var VariantSwatchAvailability = (function () {
    function VariantSwatchAvailability() {
      // Reflect-based super() so this works as a native custom element.
      return Reflect.construct(HTMLElement, [], VariantSwatchAvailability);
    }

    VariantSwatchAvailability.prototype = Object.create(HTMLElement.prototype);
    VariantSwatchAvailability.prototype.constructor = VariantSwatchAvailability;

    VariantSwatchAvailability.prototype.connectedCallback = function () {
      this._cleanup = [];
      this.wrapper = this.closest('.product__variants-wrapper') || this;

      this.variants = this.readVariants();
      if (!this.variants.length) return;

      // Options rendered as clickable buttons (swatch/chip) — these are the "anchors"
      // the shopper drives; the change we react to must come from one of them.
      this.buttonOptions = this.getButtonOptions();
      if (!this.buttonOptions.length) return;

      // Capture phase: the theme dispatches the swatch's option `change` event without
      // bubbling (it relies on listeners bound directly to each <select>), so a normal
      // bubble-phase listener on the wrapper would never see it. Capturing fires on the
      // way down regardless of the event's `bubbles` flag. By this point the theme's
      // OptionButtons handler has already written the new value to the <select>.
      var self = this;
      this._onChange = function (e) { self.onChange(e); };
      this.wrapper.addEventListener('change', this._onChange, true);
      this._cleanup.push(function () {
        self.wrapper.removeEventListener('change', self._onChange, true);
      });
    };

    VariantSwatchAvailability.prototype.disconnectedCallback = function () {
      (this._cleanup || []).forEach(function (fn) { fn(); });
      this._cleanup = [];
    };

    VariantSwatchAvailability.prototype.readVariants = function () {
      var el = this.wrapper.querySelector('[data-variant-json]');
      if (!el) return [];
      try { return JSON.parse(el.textContent || '[]') || []; } catch (e) { return []; }
    };

    // e.g. ['option1'] for a colour-swatch + size-dropdown product.
    VariantSwatchAvailability.prototype.getButtonOptions = function () {
      var groups = this.wrapper.querySelectorAll('[data-option-buttons][data-product-option]');
      var out = [];
      Array.prototype.forEach.call(groups, function (g) {
        var opt = g.getAttribute('data-product-option'); // "option1" | "option2" | ...
        if (opt && out.indexOf(opt) === -1) out.push(opt);
      });
      return out;
    };

    VariantSwatchAvailability.prototype.selectFor = function (index) {
      return this.wrapper.querySelector('#option' + index);
    };

    // Current [option1, option2, option3] values (null where the option is absent).
    VariantSwatchAvailability.prototype.currentValues = function () {
      var values = [];
      for (var i = 1; i <= 3; i++) {
        var sel = this.selectFor(i);
        values.push(sel ? sel.value : null);
      }
      return values;
    };

    VariantSwatchAvailability.prototype.matchVariant = function (values) {
      return this.variants.filter(function (v) {
        return (values[0] == null || v.option1 === values[0]) &&
               (values[1] == null || v.option2 === values[1]) &&
               (values[2] == null || v.option3 === values[2]);
      })[0] || null;
    };

    VariantSwatchAvailability.prototype.onChange = function (e) {
      var target = e.target;
      if (!target || !target.id || target.id.indexOf('option') !== 0) return;
      // Only react to a change originating from a button-style (swatch/chip) option.
      if (this.buttonOptions.indexOf(target.id) === -1) return;

      var values = this.currentValues();
      // The current full combination already maps to a real variant — leave the
      // shopper's other selections untouched (in stock → "Add to Cart", existing but
      // out of stock → "Sold out"). Only a non-existent combination needs correcting.
      if (this.matchVariant(values)) return;

      var anchorIndex = parseInt(target.id.replace('option', ''), 10); // 1-based
      var anchorKey = 'option' + anchorIndex;
      var anchorValue = target.value;

      // Variants that share the just-selected anchor value.
      var candidates = this.variants.filter(function (v) {
        return v[anchorKey] === anchorValue;
      });
      if (!candidates.length) return;

      // Prefer an in-stock variant; else fall back to the first existing one so the
      // button reads "Sold out" instead of "Unavailable".
      var chosen = candidates.filter(function (v) { return v.available; })[0] || candidates[0];

      // Snap every OTHER option's <select> to the chosen variant and dispatch change so
      // the theme re-resolves the variant, button text, price and image.
      for (var i = 1; i <= 3; i++) {
        if (i === anchorIndex) continue;
        var select = this.selectFor(i);
        if (!select) continue;
        var newVal = chosen['option' + i];
        if (newVal == null || select.value === newVal) continue;
        select.value = newVal;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    return VariantSwatchAvailability;
  })();

  customElements.define('variant-swatch-availability', VariantSwatchAvailability);
})();
